import produce from 'immer'
import { assign, filter, forEach, get, isEmpty, map, set, uniqBy } from 'lodash'

import {
  buildCategoriesTreeHierarchy,
  transformTree,
} from '../../../common/helpers'

import {
  ADD_DIET_OR_DIET_CATEGORY_NODE,
  DELETE_DIET,
  DELETE_DIET_CATEGORY,
  EXPAND_DIET_AND_DIET_CATEGORY_MAIN_TREE_KEYS,
  EXPAND_DIET_AND_DIET_CATEGORY_SUPPORTING_TREE_KEYS,
  FETCH_DIETS_AND_DIET_CATEGORIES,
  MOVE_DIETS,
  RENAME_DIET_OR_DIET_CATEGORY_NODE,
  RESET_DIETS_AND_DIET_CATEGORIES_TREE,
  SAVE_DIETS_AND_DIET_CATEGORIES,
  SET_DATA_FOR_RENAMING_DIET_OR_DIET_CATEGORY_NODE,
  SET_IS_CURATION_ENABLED_DIET,
} from '../action-types'
import { DIETS_AND_DIET_CATEGORIES_INITIAL_STATE } from '../initial-states'

const dietsAndDietCategoriesReducer = (
  state = DIETS_AND_DIET_CATEGORIES_INITIAL_STATE,
  action,
) => {
  switch (action.type) {
    case FETCH_DIETS_AND_DIET_CATEGORIES.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
          initialData: [],
          data: [],
          latestEvent: undefined,
        }),
      )

    case FETCH_DIETS_AND_DIET_CATEGORIES.COMPLETE:
      return produce(state, nextState => {
        const { results, latestEvent } =
          action.payload.data.dietsAndDietCategories

        const treeData = buildCategoriesTreeHierarchy(results)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          latestEvent,
        })
      })

    case FETCH_DIETS_AND_DIET_CATEGORIES.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
          initialData: [],
          data: [],
          latestEvent: undefined,
        }),
      )

    case FETCH_DIETS_AND_DIET_CATEGORIES.CLEANUP:
      return { ...DIETS_AND_DIET_CATEGORIES_INITIAL_STATE }

    case SAVE_DIETS_AND_DIET_CATEGORIES.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
          latestEvent: undefined,
        }),
      )

    case SAVE_DIETS_AND_DIET_CATEGORIES.COMPLETE:
      return produce(state, nextState => {
        const { results, latestEvent } =
          action.payload.data.saveDietsAndDietCategories

        const treeData = buildCategoriesTreeHierarchy(results)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
          latestEvent,
        })
      })

    case SAVE_DIETS_AND_DIET_CATEGORIES.FAIL:
      return produce(state, nextState => {
        const { graphQLErrors, message } = action.error
        const entities = get(graphQLErrors, '[0].extensions.exception.entities')

        return assign(nextState, {
          isLoading: false,
          error: {
            message,
            entities,
          },
        })
      })

    case DELETE_DIET.START:
    case DELETE_DIET_CATEGORY.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case DELETE_DIET.COMPLETE:
    case DELETE_DIET_CATEGORY.COMPLETE:
      return produce(state, nextState => {
        const { results } =
          action.payload.data.deleteDiet ||
          action.payload.data.deleteDietCategory

        const treeData = buildCategoriesTreeHierarchy(results)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case DELETE_DIET.FAIL:
    case DELETE_DIET_CATEGORY.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case SET_IS_CURATION_ENABLED_DIET.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case SET_IS_CURATION_ENABLED_DIET.COMPLETE:
      return produce(state, nextState => {
        const updatedDiet = action.payload.data.setIsCurationEnabledDiet

        const treeData = transformTree(nextState.initialData, node =>
          node.id === updatedDiet.id
            ? {
                ...node,
                isCurationEnabled: updatedDiet.isCurationEnabled,
              }
            : node,
        )

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case SET_IS_CURATION_ENABLED_DIET.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case ADD_DIET_OR_DIET_CATEGORY_NODE:
      return produce(state, nextState => {
        const { parentPath } = action.payload
        const isRootNodeAdded = isEmpty(parentPath)

        const arrayToAddNodeTo = isRootNodeAdded
          ? nextState.data
          : get(nextState.data, `${parentPath}.children`, [])

        arrayToAddNodeTo.push(action.payload)

        const treeDataWithNewNode = isRootNodeAdded
          ? arrayToAddNodeTo
          : set(nextState.data, `${parentPath}.children`, arrayToAddNodeTo)

        return assign(nextState, {
          ...nextState,
          data: treeDataWithNewNode,
          isDirty: true,
          dataForRenaming: { id: action.payload.id, type: action.payload.type },
        })
      })

    case RENAME_DIET_OR_DIET_CATEGORY_NODE:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          data: set(
            nextState.data,
            `${action.payload.path}.name`,
            action.payload.name,
          ),
          isDirty: true,
        }),
      )

    case SET_DATA_FOR_RENAMING_DIET_OR_DIET_CATEGORY_NODE:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          dataForRenaming: action.payload,
        }),
      )

    case MOVE_DIETS:
      return produce(state, nextState => {
        const { nodesToMove, destinationNode } = action.payload
        let newTreeData = nextState.data

        // remove nodes to be moved
        forEach(nodesToMove, ({ id: idToMove, parentPath }) => {
          const isMovingRootNode = isEmpty(parentPath)

          if (isMovingRootNode) {
            newTreeData = filter(newTreeData, ({ id }) => id !== idToMove)
          } else {
            newTreeData = set(
              newTreeData,
              `${parentPath}.children`,
              filter(
                get(newTreeData, `${parentPath}.children`),
                ({ id }) => id !== idToMove,
              ),
            )
          }
        })

        const isMovedToRoot = destinationNode.level === 1

        if (isMovedToRoot) {
          newTreeData = buildCategoriesTreeHierarchy([
            ...newTreeData,
            ...map(nodesToMove, node => ({
              ...node,
              parentId: null,
              level: 1,
              idPath: [node.id],
              namePath: [node.name],
            })),
          ])
        } else {
          newTreeData = transformTree(newTreeData, node =>
            node.id === destinationNode.id
              ? {
                  ...node,
                  children: uniqBy(
                    [
                      ...(node.children || []),
                      ...map(nodesToMove, node => ({
                        ...node,
                        parentId: destinationNode.id,
                        level: destinationNode.level - 1,
                        idPath: [...destinationNode.idPath, node.id],
                        namePath: [...destinationNode.namePath, node.name],
                      })),
                    ],
                    'id',
                  ),
                }
              : node,
          )
        }

        return assign(nextState, {
          ...nextState,
          isDirty: true,
          data: newTreeData,
        })
      })

    case EXPAND_DIET_AND_DIET_CATEGORY_MAIN_TREE_KEYS:
      return produce(state, nextState =>
        assign(nextState, {
          mainTreeExpandedKeys: action.payload,
        }),
      )

    case EXPAND_DIET_AND_DIET_CATEGORY_SUPPORTING_TREE_KEYS:
      return produce(state, nextState =>
        assign(nextState, {
          supportingTreeExpandedKeys: action.payload,
        }),
      )

    case RESET_DIETS_AND_DIET_CATEGORIES_TREE:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          error: undefined,
          data: nextState.initialData,
          isDirty: false,
          dataForRenaming: undefined,
        }),
      )

    default:
      return state
  }
}

export default dietsAndDietCategoriesReducer
